import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import BottomControls from "components/UI/_TODO/WorkOrMaterialsModals/components/BottomControls/BottomControls";
import { workFields, worksFormCols } from "pages/HandBook/HandBook";
import { handBookActions } from "pages/HandBook/model/slice";
import { approveCheckedExpenditures, getHandbooksAggregate } from "pages/HandBook/model/thunks";
import { HANDBOOK_EXPENDITURE_APPROVAL_STATUS } from "pages/HandBook/model/types";
import HandBookList, { IHandbookColumn } from "pages/HandBook/widgets/HandBookList/HandBookList";

import { useTypedSelector } from "app/store/typedUseSelector";
import ButtonBase from "shared/ui/controls/ButtonBase";
import ContentWithCount from "shared/ui/dataDisplay/ContentWithCount/ContentWithCount";
import Checkbox from "shared/ui/inputs/Checkbox/Checkbox";
import Modal from "shared/ui/modal/Modal";

import { LOCALIZATION_CONFIG } from "constants/localization";
import { VIEW_HANDBOOK_APPROVALS } from "constants/permissions/generalPermissions";

import usePermission from "hooks/usePermission";

import { transformDigitToFinancial } from "utils/formatters/transformDigitToFinancial";

import { ReactComponent as ApproveIcon } from "images/icons/required-day-icon-blue.svg";

import styles from "./HandBookApproveButton.module.scss";

const modalColPercentsHead = [80, 6, 8];
const modalColPercents = [80, 6, 8];

const HandBookApproveButton = () => {
  const hasHandbookApprovalPermission = usePermission(VIEW_HANDBOOK_APPROVALS);
  const dispatch = useDispatch();
  const [isOpen, setOpen] = useState(false);
  const isLoading = useTypedSelector((state) => state.handBookReducer.loadings)["getHandbooksAggregate"];
  const isPending = useTypedSelector((state) => state.handBookReducer.loadings)["approveCheckedExpenditures"];
  const invalidationKey = useTypedSelector((state) => state.handBookReducer.invalidationKey);
  const lastUsedKey = useTypedSelector((state) => state.handBookReducer._lastUsedKey);
  const handbooks = useTypedSelector((state) => state.handBookReducer.handbooks);
  const loadedHandbooksCount = Number((lastUsedKey && handbooks[lastUsedKey]?.results.length) || 0);

  const editings = useTypedSelector((state) => state.handBookReducer.editings);
  const isEditing = editings.isEditing;
  const aggregations = useTypedSelector((state) => state.handBookReducer.handbookAggregate);
  const checkedCount = useMemo(() => Object.values(editings.checked).filter((v) => v).length, [editings.checked]);

  const closeModal = () => {
    setOpen(false);
    dispatch(handBookActions.clearEditings());
  };

  const approveExpenditures = () => {
    dispatch(approveCheckedExpenditures({ successCallback: closeModal }));
  };

  useEffect(() => {
    dispatch(getHandbooksAggregate());
  }, [invalidationKey]);

  useEffect(() => {
    dispatch(handBookActions.clearEditings());
  }, [isOpen]);

  const modalWorkCols: IHandbookColumn[] = [
    { header: { title: "Наименование", filterName: "name" }, render: (item) => item.name },
    { header: { title: "Ед. изм." }, render: (item) => item.measure, isCentered: true },
    {
      header: { title: `Цена ${LOCALIZATION_CONFIG.currency}` },
      render: (item) => transformDigitToFinancial(item.price),
      isRighted: true,
    },
  ];
  // Скрываем кнопку согласования в режиме редактирования тк при слогасовании перезагружается Справочник и редактирование будет утеряно
  if (!hasHandbookApprovalPermission || isEditing) return null;

  return (
    <>
      <ContentWithCount
        count={aggregations.in_review_count}
        isCountDisplayed={aggregations.in_review_count > 0}
        absoluteContainerClassName={styles.approveCount}
      >
        <ButtonBase
          secondary
          disabled={!aggregations.in_review_count}
          onClick={() => setOpen(true)}
          title="Согласование"
          isLoading={isLoading}
        >
          <ApproveIcon className={styles.controlIcon} />
        </ButtonBase>
      </ContentWithCount>

      <Modal
        isOpen={!!isOpen}
        onClose={closeModal}
        className={styles.modal}
        headerClassName={styles.header}
        title={
          <>
            <h3>Согласование</h3>
            <p>(Позиций: {aggregations.in_review_count})</p>
          </>
        }
      >
        <div className={styles.list}>
          <HandBookList
            tableHeadClassName={styles.listHead}
            fields={workFields} // не используется в списке в модалке
            columns={modalWorkCols}
            defaultFilter={{ status: HANDBOOK_EXPENDITURE_APPROVAL_STATUS.IN_REVIEW }} // TODO: Разделить согласования по type по табам
            colPercentsHead={modalColPercentsHead}
            colPercents={modalColPercents}
            formColPercents={worksFormCols} // не используется в списке в модалке
            canCheckRows
          />
        </div>
        <BottomControls isExists isDoubleBtns noWrapper className={styles.bottomControls}>
          <ButtonBase secondary onClick={closeModal}>
            Отменить
          </ButtonBase>
          <ButtonBase primary disabled={!checkedCount} onClick={approveExpenditures} isLoading={isPending}>
            {checkedCount ? `Согласовать: ${checkedCount}` : `Согласовать`}
          </ButtonBase>
        </BottomControls>
      </Modal>
    </>
  );
};

export default React.memo(HandBookApproveButton);
